import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/store/useUserStore';
import { storeToRefs } from 'pinia';
import axios from 'axios';
import HomeView from '../views/HomeView.vue';

const routes: Array<RouteRecordRaw> = [
	{
		path: '/auth',
		meta: { login: true },
		component: () => import('../layouts/AuthLayout.vue'),
		children: [
			{
				path: 'login',
				name: 'authLogin',
				meta: { free: true },
				component: () => import('../views/Auth/LoginView.vue'),
			},
			{
				path: 'company',
				name: 'authCompanySelect',
				meta: { free: true },
				component: () => import('../views/Auth/CompanySelectView.vue'),
			},
			{
				path: 'register',
				name: 'authRegister',
				meta: { free: true },
				component: () => import('../views/Auth/RegisterView.vue'),
			},
			{
				path: 'register/:month',
				name: 'authRegisterSubscitpion',
				meta: { free: true },
				component: () => import('../views/Auth/RegisterView.vue'),
			},
			{
				path: 'reset',
				name: 'authReset',
				meta: { free: true },
				component: () => import('../views/Auth/ResetView.vue'),
			},
			{
				path: 'password/:hash',
				name: 'authPassword',
				meta: { free: true },
				component: () => import('../views/Auth/PasswordView.vue'),
			},
			{
				path: 'activate/:hash',
				name: 'authActivate',
				meta: { free: true },
				component: () => import('../views/Auth/ActivateView.vue'),
			},
		],
	},
	{
		path: '/',
		meta: { auth: true },
		component: () => import('../layouts/AppLayout.vue'),
		children: [
			{
				path: '',
				name: 'dashboard',
				component: () => import('../views/App/Dashboard/IndexView.vue'),
			},
			{
				path: 'register',
				component: () => import('../views/App/Register/DefaultView.vue'),
				children: [
					{
						path: '',
						name: 'registerIndex',
						component: () => import('../views/App/Register/IndexView.vue'),
					},
					{
						path: 'create',
						name: 'registerCreate',
						component: () => import('../views/App/Register/CreateView.vue'),
					},
					{
						path: ':hash',
						name: 'registerUpdate',
						component: () => import('../views/App/Register/UpdateView.vue'),
					},
					{
						path: 'report',
						name: 'reportIndex',
						component: () => import('../views/App/Report/IndexView.vue'),
					},
					{
						path: 'declaration',
						name: 'declarationIndex',
						component: () => import('../views/App/Declaration/IndexView.vue'),
					},
					{
						path: 'location',
						component: () => import('../views/App/Location/DefaultView.vue'),
						children: [
							{
								path: '',
								name: 'locationIndex',
								component: () => import('../views/App/Location/IndexView.vue'),
							},
							{
								path: 'create',
								name: 'locationCreate',
								component: () => import('../views/App/Location/CreateView.vue'),
							},
							{
								path: ':hash',
								name: 'locationUpdate',
								component: () => import('../views/App/Location/UpdateView.vue'),
							},
						],
					},
				],
			},
			{
				path: 'user',
				component: () => import('../views/App/User/DefaultView.vue'),
				children: [
					{
						path: '',
						name: 'userIndex',
						component: () => import('../views/App/User/IndexView.vue'),
					},
					{
						path: 'create',
						name: 'userCreate',
						component: () => import('../views/App/User/CreateView.vue'),
					},
					{
						path: ':hash',
						name: 'userUpdate',
						component: () => import('../views/App/User/UpdateView.vue'),
					},
				],
			},
			{
				path: 'admin',
				component: () => import('../views/App/Admin/DefaultView.vue'),
				children: [
					{
						path: 'user',
						meta: { free: true },
						name: 'adminUser',
						component: () => import('../views/App/Admin/UserView.vue'),
					},
				],
			},
			{
				path: 'setting',
				component: () => import('../views/App/Setting/DefaultView.vue'),
				children: [
					{
						path: 'company',
						meta: { free: true },
						name: 'settingCompany',
						component: () => import('../views/App/Setting/CompanyView.vue'),
					},
					{
						path: 'account',
						meta: { free: true },
						name: 'settingAccount',
						component: () => import('../views/App/Setting/AccountView.vue'),
					},
					{
						path: 'subscription',
						component: () => import('../views/App/Setting/Subscription/DefaultView.vue'),
						children: [
							{
								path: '',
								meta: { free: true },
								name: 'settingSubscription',
								component: () => import('../views/App/Setting/Subscription/IndexView.vue'),
							},
							{
								path: 'renew/:month',
								meta: { free: true },
								name: 'settingSubscriptionRenew',
								component: () => import('../views/App/Setting/Subscription/RenewView.vue'),
							},
							{
								path: 'complete',
								meta: { free: true },
								name: 'settingSubscriptionComplete',
								component: () => import('../views/App/Setting/Subscription/CompleteView.vue'),
							},
						],
					},
				],
			},
			{
				path: 'contact',
				name: 'contact',
				meta: { free: true },
				component: () => import('../views/App/Page/ContactView.vue'),
			},
			{
				path: 'faq',
				name: 'faq',
				meta: { free: true },
				component: () => import('../views/App/Page/FaqView.vue'),
			},
		],
	},
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
});

router.beforeEach(async (to, from, next) => {
	const storeUser = useUserStore();
	const { authenticated, user } = storeToRefs(storeUser);
	const token = localStorage.getItem('auth._token');
	const isCompanySelection = to.name === 'authCompanySelect';
	const isFreeRoute = to.matched.some((record) => record.meta.free);
	const requiresAuthentication = to.matched.some((record) => record.meta.auth);
	const continueNavigation = () => {
		window.scrollTo(0, 0);
		next();
	};

	if (token) {
		if (!authenticated.value) {
			try {
				const requestUser = await axios.get('/user');
				const { data } = requestUser;

				storeUser.setAuthenticated(true);
				storeUser.setUser(data);
				storeUser.setToken(token);
			} catch (err: any) {
				if (err.response?.status === 401) {
					localStorage.removeItem('auth._token');
					storeUser.setAuthenticated(false);
					storeUser.setUser({ subscription: null, read_subscription: null });
					delete axios.defaults.headers.common.Authorization;
					return next({ name: 'authLogin' });
				}

				return next(false);
			}
		}

		if (isCompanySelection) {
			return continueNavigation();
		}

		if (user.value.company_selection_required) {
			return next({ name: 'authCompanySelect' });
		}

		if (!isFreeRoute && user.value.subscription === null && user.value.read_subscription === null) {
			return next({ name: 'settingSubscription' });
		}

		return continueNavigation();
	}

	if (requiresAuthentication || isCompanySelection) {
		return next({ name: 'authLogin' });
	}

	return continueNavigation();
});

export default router;
